import { useCallback, useMemo, useRef, useState } from 'react';
import mapTranslationsRecordToLoadedTranslationsRecord from '../../map/map-translations-record-to-loaded-translations-record.js';
import RunnableTranslateFunction from '../../runnables/runnable-translate-function.js';
import handleNotFound from '../../utils/handle-not-found.js';
import useLoadTranslations from './hooks/use-load-translations.js';
export default function useProvider({ fallbackLocale, locale, onLoadError, translationsRecord, }) {
    if (typeof translationsRecord[locale] === 'undefined') {
        throw new Error(`Translations do not exist for locale: ${String(locale)}`);
    }
    if (typeof fallbackLocale === 'string' &&
        typeof translationsRecord[fallbackLocale] === 'undefined') {
        throw new Error(`Translations do not exist for fallback locale: ${fallbackLocale}`);
    }
    const asyncLoadFallbackTranslationsEffect = useRef(null);
    const asyncLoadTranslationsEffect = useRef(null);
    const [loadedTranslationsRecord, setLoadedTranslationsRecord] = useState(() => mapTranslationsRecordToLoadedTranslationsRecord(translationsRecord));
    const loadTranslations = useLoadTranslations({
        onLoad: useCallback((newLocale, newTranslations) => {
            setLoadedTranslationsRecord((oldLoadedTranslationsRecord) => ({
                ...oldLoadedTranslationsRecord,
                [newLocale]: newTranslations,
            }));
        }, []),
        onLoadError,
        translationsRecord,
    });
    const getLoadedFallbackTranslations = () => {
        if (typeof fallbackLocale === 'undefined') {
            return;
        }
        return loadedTranslationsRecord[fallbackLocale];
    };
    const loadedFallbackTranslations = getLoadedFallbackTranslations();
    const loadedTranslations = loadedTranslationsRecord[locale];
    return {
        asyncLoadFallbackTranslationsEffect,
        asyncLoadTranslationsEffect,
        translate: useMemo(() => {
            const newTranslate = new RunnableTranslateFunction({
                fallbackTranslations: loadedFallbackTranslations,
                translations: loadedTranslations,
            });
            newTranslate.on('loadFallbackTranslations', (str) => {
                if (typeof fallbackLocale === 'undefined') {
                    throw new Error(`Translation not found: ${str}`);
                }
                asyncLoadFallbackTranslationsEffect.current =
                    loadTranslations(fallbackLocale);
            });
            newTranslate.on('loadTranslations', () => {
                asyncLoadTranslationsEffect.current = loadTranslations(locale);
            });
            newTranslate.on('notFound', handleNotFound);
            return newTranslate.run.bind(newTranslate);
        }, [
            fallbackLocale,
            loadTranslations,
            loadedFallbackTranslations,
            loadedTranslations,
            locale,
        ]),
    };
}
//# sourceMappingURL=provider.hook.js.map